<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg p-5 flex flex-col w-full mt-4 gap-4">
    <div class="flex flex-col gap-4">
      <div>
        <div class="flex justify-between flex-wrap mx-auto items-end gap-3" dir="ltr">
          <VueDatePicker
            class="max-w-sm"
            auto-apply
            :day-names="['ن', 'ث', 'ر', 'خ', 'ج', 'س', 'ح']"
            locale="ar"
            :ui="{
              input:
                'text-primary dark:text-blue-400 text-3xl font-bold text-center dark:!bg-gray-700',
              navBtnNext: ' rtl:rotate-180 rtl:transform rtl:text-right dark:text-gray-200',
              navBtnPrev: ' rtl:rotate-180 rtl:transform rtl:text-right dark:text-gray-200'
            }"
            :dark="isDark"
            placeholder="اختر نطاق التقرير"
            range
            select-text="اختر"
            cancel-text="الغاء"
            v-model="range"
            :preview-format="
            (date: Date[]) =>
              `من ${moment(date?.[0]).format('YYYY-MM-DD')} إلى ${moment(date?.[1]).format('YYYY-MM-DD')}`
          "
            :format="
            (date: Date[]) =>
              `من ${moment(date?.[0]).format('YYYY-MM-DD')} إلى ${moment(date?.[1]).format('YYYY-MM-DD')}`
          "
            :enable-time-picker="false"
            :time-picker="false"
            focus-start-date
            :max-date="new Date()"
          >
          </VueDatePicker>

          <div class="flex gap-4 flex-wrap sm:flex-nowrap sm:w-1/2 w-full" dir="rtl">
            <Select
              label="الفرع"
              :items="[
                { id: undefined, name: 'الكل' },
                ...(user?.user_branches.allowed_branches.map((b) => ({
                  id: b?.[0],
                  name: b?.[1]
                })) ?? [])
              ]"
              item-label="name"
              item-value="id"
              v-if="(user?.user_branches?.allowed_branches?.length ?? 0) > 1"
              class="max-w-none w-full sm:max-w-96"
              v-model.number="selectedBranch"
            />

            <SessionsPicker
              :sessions="sessions ?? []"
              :multiple="false"
              class="max-w-none w-full sm:max-w-96"
              v-model.number="selectedSessionId"
            />
          </div>
        </div>
      </div>
      <div class="sm:p-4">
        <!-- <canvas class="" id="chart" ref="canvas"></canvas> -->

        <div class="flex justify-between gap-2 flex-wrap sm:flex-nowrap">
          <div
            class="flex flex-col gap-2 border dark:border-gray-700 rounded-lg p-3 items-center w-full dark:text-gray-200"
          >
            <div>الحضور</div>
            <div class="text-green-500">
              {{ formatPercentage(data[0], evaluations?.length as number) }}
            </div>
          </div>

          <div
            class="flex flex-col gap-2 border dark:border-gray-700 rounded-lg p-3 items-center w-full dark:text-gray-200"
          >
            <div>الغياب</div>
            <div class="text-red-500">
              {{ formatPercentage(data[1], evaluations?.length as number) }}
            </div>
          </div>

          <div
            class="flex flex-col gap-2 border dark:border-gray-700 rounded-lg p-3 items-center w-full dark:text-gray-200"
          >
            <div>التأخير</div>
            <div class="text-blue-500">
              {{ formatPercentage(data[2], evaluations?.length as number) }}
            </div>
          </div>

          <div
            class="flex flex-col gap-2 border dark:border-gray-700 rounded-lg p-3 items-center w-full dark:text-gray-200"
          >
            <div>الاستئذان</div>
            <div class="text-yellow-500">
              {{ formatPercentage(data[3], evaluations?.length as number) }}
            </div>
          </div>
        </div>
      </div>

      <AttendanceBreakdown v-if="evaluations" :evaluations="evaluations"></AttendanceBreakdown>

      <div class="sm:p-4 h-96 sm:h-[40rem]">
        <canvas class="" id="chart" ref="canvas"></canvas>

        <div class="flex justify-between gap-2 flex-wrap sm:flex-nowrap mt-3">
          <Button @click="pageNumber++" :disabled="totalPages === pageNumber"> التالي </Button>

          <Button @click="pageNumber--" :disabled="pageNumber === 1"> السابق </Button>
        </div>
      </div>
      <h5 class="mb-0 dark:text-gray-100 text-2xl font-bold mt-14">ترتيب الطلاب</h5>
      <Datatable :headers="columns" :data="studentsClassification" />

      <DegreeDistribution v-if="evaluations" :evaluations="evaluations"></DegreeDistribution>
    </div>
  </div>
  <div class="bg-white dark:bg-gray-800 rounded-lg p-5 flex flex-col w-full mt-4 gap-4">
    <PagesStatistics :evaluations="evaluations ?? []" />
  </div>
</template>

<script setup lang="tsx">
import type { Session, Student } from '@/types/global'
import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import Chart from 'chart.js/auto'
import moment from 'moment'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { useQuery } from '@tanstack/vue-query'
import { getRangeEvaluations } from '@/services/evaluations'
import Button from '../Button.vue'
import { useSelectedSessionId } from '@/composables/useSelectedSessionId'
import { fetchSessions } from '@/services/sessions'
import Select from '../shared/Select.vue'
import SessionsPicker from '../shared/SessionsPicker.vue'
import { formatPercentage } from '@/utils/formatPercentage'
import { groupBy, sumBy } from 'lodash'
import { fetchPlans } from '@/services/plans'
import { createColumnHelper } from '@tanstack/vue-table'
import Datatable from '../shared/Datatable.vue'
import { useDark, useMediaQuery, useStorage } from '@vueuse/core'
import DegreeDistribution from './DegreeDistribution.vue'
import AttendanceBreakdown from './AttendanceBreakdown.vue'
import { storeToRefs } from 'pinia'
import { auth } from '@/stores/auth'
import PagesStatistics from '@/components/charts/PagesStatistics.vue'

const range = ref([moment().subtract(1, 'month').toDate(), moment().toDate()])
const canvas = ref<HTMLCanvasElement | null>(null)

const isDark = useDark()

let chart = shallowRef<Chart | undefined>(undefined)

const { user } = storeToRefs(auth())

const selectedBranch = useStorage<number | undefined>('selected-branch', undefined)

watch(range, (value) => {
  if (chart.value) {
    chart.value.data.labels = generateDateRange.value
    chart.value.update()
  }
})

function printMaxAndMinDates(arr: any[]) {
  if (!Array.isArray(arr) || arr.length === 0) {
    console.error('Invalid or empty array')
    return
  }

  const dates = arr.map((obj) => moment(obj.date)).filter((m) => m.isValid())

  if (dates.length === 0) {
    console.error('No valid dates found in the array')
    return
  }

  const minDate = moment.min(dates)
  const maxDate = moment.max(dates)

  console.log('Minimum Date:', minDate.format('YYYY-MM-DD HH:mm:ss'))
  console.log('Maximum Date:', maxDate.format('YYYY-MM-DD HH:mm:ss'))
}

const isMobileScreen = useMediaQuery('(max-width: 768px)')
const ITEMS_PER_PAGE = computed(() => {
  return isMobileScreen.value ? 5 : 12
})
// const selectedSessionId = useSelectedSessionId()
const selectedSessionId = useStorage<number | undefined>('selected-session-id', undefined, undefined, {
  serializer: {
    read: (v: string) => (v === 'undefined' || v === 'null' ? undefined : JSON.parse(v)),
    write: (v: number | undefined) => (v === undefined ? 'null' : JSON.stringify(v))
  }
})

const { data: sessions } = useQuery({
  queryKey: ['presence-chart-sessions', selectedBranch],
  queryFn: async () => await fetchSessions(['name', 'teacher_id'], selectedBranch.value),
  select(data) {
    // if (selectedSessionId.value == -2) {
    //   selectedSessionId.value = data[0].id
    // }

    return data
  }
})

const canFetchEvaluations = computed(() => {
  return !!chart && !!sessions.value && !!sessions.value?.length && sessions.value.length > 0
})

const { data: evaluations, refetch: refetchEvaluations } = useQuery({
  queryKey: ['presence-chart-evaluations', range, selectedSessionId],
  queryFn: async () => {
    return await getRangeEvaluations(
      moment(range.value[0]).format('YYYY-MM-DD'),
      moment(range.value[1]).format('YYYY-MM-DD'),
      !selectedSessionId.value
        ? [
            [
              'session_id',
              'in',
              sessions.value?.map((s) => parseInt(s.id as unknown as string)) ?? []
            ]
          ]
        : [['session_id', 'in', [parseInt(selectedSessionId.value as unknown as string)]]],
      [
        'date',
        'degree_memorize',
        'degree_review',
        'degree_ensure',
        'total_review_page',
        'total_memorize_page',
        'presence_type',
        'total_ensure_page',
        'student_id'
      ]
    )
  },

  enabled: canFetchEvaluations
})

// const { data: plans } = useQuery({
//   queryKey: ['plans', selectedSessionId, range],
//   queryFn: async () => {
//     if (!sessions.value || !evaluations.value) return []

//     const plans = fetchPlans(
//       [selectedSessionId.value],
//       moment(moment(range.value?.[0])).format('YYYY-MM-DD'),
//       moment(moment(range.value?.[1])).format('YYYY-MM-DD')
//     )

//     return plans
//   },
//   enabled: computed(() => !!sessions.value && !!evaluations.value)
// })

const data = computed(() => {
  let presenceCount = 0
  let absenceCount = 0
  let lateCount = 0

  evaluations.value?.forEach((evaluation) => {
    if (evaluation.presence_type === 'present') {
      presenceCount++
    } else if (evaluation.presence_type === 'absent') {
      absenceCount++
    } else if (evaluation.presence_type === 'late') {
      lateCount++
    }
  })

  return [
    presenceCount,
    absenceCount,
    lateCount,
    (evaluations.value?.length ?? 0) - (presenceCount + absenceCount + lateCount)
  ]
})

const generateDateRange = computed(() => {
  const start = moment(range.value[0])
  const end = moment(range.value[1])
  const dates = []

  while (start.isSameOrBefore(end)) {
    dates.push(start.format('YYYY-MM-DD'))
    start.add(1, 'day')
  }

  return dates
})

const pageNumber = ref(1)

const totalPages = computed(() => {
  return Math.ceil(generateDateRange.value.length / ITEMS_PER_PAGE.value)
})

const activePage = computed(() => {
  const startIndex = (pageNumber.value - 1) * ITEMS_PER_PAGE.value
  const endIndex = startIndex + ITEMS_PER_PAGE.value
  return generateDateRange.value.slice(startIndex, endIndex)
})

watch(activePage, (value) => {
  if (
    chart.value &&
    chart.value.options &&
    chart.value.options.scales &&
    chart.value.options.scales.x
  ) {
    const startIndex = (pageNumber.value - 1) * ITEMS_PER_PAGE.value
    const endIndex = startIndex + ITEMS_PER_PAGE.value

    chart.value.options.scales.x.min = startIndex
    chart.value.options.scales.x.max = endIndex

    chart.value.update()
  }
})

const memorizeStats = computed(() => {
  console.log(generateDateRange.value)

  return generateDateRange.value.map((date) => {
    return (
      evaluations.value
        ?.filter((e) => {
          return e.date === date
        })
        ?.reduce((acc, e) => {
          return acc + (e.total_memorize_page ?? 0)
        }, 0) ?? 0
    )
  })
})

watch(memorizeStats, (value) => {
  if (chart.value) {
    chart.value.data.datasets[0].data = value
    chart.value.update()
  }
})

const ensureStats = computed(() => {
  return generateDateRange.value.map((date) => {
    return (
      evaluations.value
        ?.filter((e) => e.date === date)
        ?.reduce((acc, e) => {
          return acc + (e.total_ensure_page ?? 0)
        }, 0) ?? 0
    )
  })
})

watch(ensureStats, (value) => {
  if (chart.value) {
    chart.value.data.datasets[1].data = value
    chart.value.update()
  }
})

const reviewStats = computed(() => {
  return generateDateRange.value.map((date) => {
    return (
      evaluations.value
        ?.filter((e) => e.date === date)
        ?.reduce((acc, e) => {
          return acc + (e.total_review_page ?? 0)
        }, 0) ?? 0
    )
  })
})

watch(reviewStats, (value) => {
  if (chart.value) {
    chart.value.data.datasets[2].data = value
    chart.value.update()
  }
})

onMounted(() => {
  if (!canvas.value) return

  chart.value = new Chart(canvas.value, {
    type: 'bar',
    options: {
      responsive: true,
      maintainAspectRatio: false, // Disable aspect ratio

      plugins: {
        title: {
          text: 'عدد الأوجه بالفترة',
          display: true,
          font: {
            size: 16
          }
        }
      },
      scales: {
        x: {
          min: 0,
          max: ITEMS_PER_PAGE.value
        }
      }
    },

    data: {
      datasets: [
        {
          data: memorizeStats.value,
          label: 'عدد أوجه الحفظ'
        },
        {
          data: ensureStats.value,
          label: 'عدد أوجه التثبيت'
        },
        {
          data: reviewStats.value,
          label: 'عدد أوجه المراجعة'
        }
      ],
      labels: generateDateRange.value
    }
  })
})
const studentsClassification = computed(() => {
  const groupedEvaluations = groupBy(evaluations.value ?? [], (e) => e.student_id.name)

  console.log(groupedEvaluations)

  let maxEvaluations = 0

  for (const name of Object.keys(groupedEvaluations)) {
    maxEvaluations =
      groupedEvaluations[name].length > maxEvaluations
        ? groupedEvaluations[name].length
        : maxEvaluations
  }

  const absences: { [key: string]: number } = {}
  const reviewRating: { [key: string]: number } = {}
  const memorizeRating: { [key: string]: number } = {}
  const ensureRating: { [key: string]: number } = {}

  const result: {
    [key: string]: {
      absence: number
      reviewRating: number
      memorizeRating: number
      ensureRating: number
      evaluationsCount: number
      maxEvaluations: number
    }
  } = {}

  for (let name of Object.keys(groupedEvaluations)) {
    absences[name] = groupedEvaluations[name].filter((s) => s.presence_type == 'absent').length

    reviewRating[name] = sumBy(groupedEvaluations[name], (s) => parseInt(s.degree_review as string))

    memorizeRating[name] = sumBy(groupedEvaluations[name], (s) =>
      parseInt(s.degree_memorize as string)
    )
    ensureRating[name] = sumBy(groupedEvaluations[name], (s) => parseInt(s.degree_ensure as string))

    result[name] = {
      absence: groupedEvaluations[name].filter((s) => s.presence_type == 'absent').length,
      reviewRating: groupedEvaluations[name].reduce((acc, e) => {
        if (!e.degree_review) return acc

        return acc + parseInt((e.degree_review as string) ?? 0)
      }, 0),
      memorizeRating: groupedEvaluations[name].reduce((acc, e) => {
        if (!e.degree_memorize) return acc
        return acc + parseInt((e.degree_memorize as string) ?? 0)
      }, 0),
      ensureRating: groupedEvaluations[name].reduce((acc, e) => {
        if (!e.degree_ensure) return acc

        return acc + parseInt((e.degree_ensure as string) ?? 0)
      }, 0),
      evaluationsCount: groupedEvaluations[name].length,
      maxEvaluations: maxEvaluations
    }
  }
  const ABSENCE_WEIGHT = 1

  const nanToZero = (value: string | number) => {
    return Number.isNaN(value) ? 0 : (value as number)
  }
  const sortedEntries = Object.entries(result).sort(([, a], [, b]) => {
    const averageA =
      (nanToZero(a.reviewRating) + nanToZero(a.ensureRating) + nanToZero(a.memorizeRating)) / 3
    const averageB =
      (nanToZero(b.reviewRating) + nanToZero(b.ensureRating) + nanToZero(b.memorizeRating)) / 3

    const scoreA = averageA - ABSENCE_WEIGHT * a.absence
    const scoreB = averageB - ABSENCE_WEIGHT * b.absence

    return scoreB - scoreA
  })

  return sortedEntries.map((e) => ({
    name: e[0],
    ...e[1]
  }))
})

const columnHelper = createColumnHelper<
  | {
      name: string
      absence: number
      reviewRating: number
      memorizeRating: number
      ensureRating: number
      evaluationsCount: number
      maxEvaluations: number
      index: number
    }
  | undefined
>()

const columns = [
  columnHelper.accessor('index', {
    id: 'number',
    header: () => 'الرتبة',
    cell: ({ row }) => row.index + 1
  }),
  columnHelper.accessor('name', {
    id: 'name',
    header: () => 'اسم الطالب'
  }),
  columnHelper.accessor('absence', {
    id: 'absence',
    header: () => 'عدد الغيابات'
  }),
  columnHelper.accessor('memorizeRating', {
    id: 'memorizeRating',
    header: () => 'معدل تقييم الحفظ',
    cell: ({ row }) => (
      <div>
        {Number.isNaN(row.original?.memorizeRating) ? 0 : row.original?.memorizeRating} /{' '}
        {(row.original?.maxEvaluations ?? 1) * 10}
      </div>
    )
  }),
  columnHelper.accessor('reviewRating', {
    id: 'reviewRating',
    header: () => 'معدل تقييم المراجعة',
    cell: ({ row }) => (
      <div>
        {Number.isNaN(row.original?.reviewRating) ? 0 : row.original?.reviewRating} /{' '}
        {(row.original?.maxEvaluations ?? 1) * 10}
      </div>
    )
  }),
  columnHelper.accessor('ensureRating', {
    id: 'ensureRating',
    header: () => 'معدل تقييم التثبيت',
    cell: ({ row }) => (
      <div>
        {Number.isNaN(row.original?.ensureRating) ? 0 : row.original?.ensureRating} /{' '}
        {(row.original?.maxEvaluations ?? 1) * 10}
      </div>
    )
  })
]
</script>

<style scoped></style>
